import { loaderStore, resetLoaderCache, type LoaderSnapshot } from '@/hooks/loader-store';
import { INBOX_LOADER_KEY } from '@/lib/inbox-api';
import { isTaskMetaKey } from '@/lib/task-meta';
import { rememberUser } from '@/lib/launch-user';
import { prefCodec, readPref, UI_PREFS, withCodec, writePref } from '@/lib/ui-prefs';
import { useAuthStore } from '@/stores/authStore';
import type { User } from '@/types/User';

/**
 * T-68: opening the app draws the Inbox at once. The Inbox and the projects list (the sidebar, its
 * Inbox count and the Inbox's project names) are remembered on this browser for the person signed
 * in, and put back into the loaders before the app first draws; each loads again behind what's
 * shown (`refreshing`). Cleared on sign-out (ui-prefs `clearOnSignOut`), when the session check
 * says signed out, and when someone else signs in here. Each project's task meta (lib/task-meta.ts:
 * its columns, people and `#tags`) is remembered too, for the projects used most recently (SF-T-172).
 */

/** The shared loader keys remembered for the next launch. */
export const LAUNCH_KEYS = [INBOX_LOADER_KEY, 'factory:projects'] as const;

/** Projects whose task meta is remembered: the most recently loaded ones. */
const MAX_TASK_META = 10;

/** A loader key worth remembering: one of LAUNCH_KEYS, or a project's task meta. */
export function isLaunchKey(key: string): boolean {
  return (LAUNCH_KEYS as readonly string[]).includes(key) || isTaskMetaKey(key);
}

/** Bigger answers aren't remembered (localStorage holds about 5 MB for the whole site). */
const MAX_ENTRY_CHARS = 1_000_000;
/** Saves wait this long, so a burst of changes (marking items read) writes once. */
const SAVE_DELAY_MS = 1_000;

const LAUNCH_DATA = withCodec(UI_PREFS.launchData, prefCodec.text);

export type LaunchData = {
  userId: string;
  entries: Record<string, { at: number; data: unknown }>;
};

/** What's remembered, read from text; null when it's missing or can't be read. */
export function parseLaunchData(raw: string | null): LaunchData | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<LaunchData> | null;
    if (!v || typeof v.userId !== 'string' || !v.entries || typeof v.entries !== 'object') return null;
    const entries: LaunchData['entries'] = {};
    for (const [key, value] of Object.entries(v.entries as Record<string, unknown>)) {
      if (!isLaunchKey(key)) continue;
      const entry = value as { at?: unknown; data?: unknown } | null | undefined;
      if (entry && typeof entry.at === 'number' && entry.data !== undefined) entries[key] = { at: entry.at, data: entry.data };
    }
    return { userId: v.userId, entries };
  } catch {
    return null;
  }
}

/** A loader's answer worth remembering: an API answer with data and no error. */
export function rememberable(snapshot: LoaderSnapshot): boolean {
  if (!snapshot.hasData || snapshot.updatedAt === undefined) return false;
  const res = snapshot.data as { data?: unknown; error?: unknown } | null | undefined;
  return Boolean(res && typeof res === 'object' && res.data !== undefined && !res.error);
}

function forgetLaunchData(): void {
  clearTimeout(saveTimer);
  saveTimer = undefined;
  writePref(LAUNCH_DATA, null);
}

let saveTimer: ReturnType<typeof setTimeout> | undefined;

/** The entries with only the MAX_TASK_META most recent projects' task meta. */
export function keepRecentTaskMeta(entries: LaunchData['entries']): LaunchData['entries'] {
  const old = Object.entries(entries)
    .filter(([key]) => isTaskMetaKey(key))
    .sort(([, a], [, b]) => b.at - a.at)
    .slice(MAX_TASK_META)
    .map(([key]) => key);
  if (!old.length) return entries;
  const kept = { ...entries };
  for (const key of old) delete kept[key];
  return kept;
}

function save(): void {
  saveTimer = undefined;
  const user = useAuthStore.getState().user;
  // Only for a confirmed session: the remembered person's own data is already what's stored.
  if (!user?.token) return;
  const previous = parseLaunchData(readPref(LAUNCH_DATA));
  const entries = previous?.userId === user.id ? { ...previous.entries } : {};
  for (const key of [...LAUNCH_KEYS, ...loaderStore.keys().filter(isTaskMetaKey)]) {
    const snapshot = loaderStore.snapshot(key);
    if (!rememberable(snapshot)) continue;
    // Seeded data has no settledAt and nothing new: keep what's stored.
    if (snapshot.settledAt === undefined && entries[key]) continue;
    if (JSON.stringify(snapshot.data).length > MAX_ENTRY_CHARS) delete entries[key];
    else entries[key] = { at: snapshot.updatedAt ?? Date.now(), data: snapshot.data };
  }
  writePref(LAUNCH_DATA, JSON.stringify({ userId: user.id, entries: keepRecentTaskMeta(entries) } satisfies LaunchData));
}

let started = false;

/**
 * Put the remembered data back into the loaders (before the first draw), then keep it up to date.
 * Once, from index.tsx.
 */
export function startLaunchCache(): void {
  if (started) return;
  started = true;

  const user = useAuthStore.getState().user;
  const stored = parseLaunchData(readPref(LAUNCH_DATA));
  if (user && stored?.userId === user.id) {
    for (const [key, entry] of Object.entries(stored.entries)) loaderStore.seed(key, entry.data, entry.at);
  }

  loaderStore.observe((key, snapshot) => {
    if (!isLaunchKey(key) || !rememberable(snapshot)) return;
    clearTimeout(saveTimer);
    saveTimer = setTimeout(save, SAVE_DELAY_MS);
  });

  useAuthStore.subscribe((state, prev) => onUserChange(state.user, prev.user));
}

/** Remember who's signed in; forget them and their data when they leave or someone else arrives. */
function onUserChange(user: User | null, prev: User | null): void {
  if (user === prev) return;
  if (!user) {
    // Signed out (here, in another tab, or the session check said so): their remembered data goes
    // from memory too, so whoever signs in next in this page never sees it. Only those keys: a public
    // page open under them (an invitation) keeps loading.
    rememberUser(null);
    forgetLaunchData();
    loaderStore.forget([...LAUNCH_KEYS, ...loaderStore.keys().filter(isTaskMetaKey)]);
    return;
  }
  if (prev && prev.id !== user.id) {
    // Someone else signed in: nothing of the last person's stays on screen or on the device, and
    // the pages still showing load again for the new person.
    resetLoaderCache();
    forgetLaunchData();
  }
  if (user.token) rememberUser(user);
}
